/**
 * Drives the Match Sport screen. The match-3 campaign lives inside a WebView game runtime; this
 * hook owns the host side of the bridge: it boots the runtime with the app-side state snapshot on
 * `ready`, mirrors every game persist back into app storage (AC-8), reports level wins to the
 * server, and syncs coins from the SERVER response only (never a client-computed total) — the
 * server decides every award + the daily cap, so a spoofed win can't mint coins.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useSoundStore } from '../../shared/stores/soundStore';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { startMatchSportRun, recordMatchSportLevelClear } from '../helpers/matchSportApi';
import { buildMatchSportGameHtml } from '../helpers/matchSportGameHtml';
import { readMatchSportState, saveMatchSportState } from '../helpers/matchSportStateStorage.helper';
import {
  MATCH_SPORT_GAME_KEY,
  MATCH_SPORT_DAILY_COIN_CAP,
  type MatchSportAudioPayload,
  type MatchSportBridgeMessage,
  type MatchSportStartPayload,
} from '../constants/matchSport.constants';

type Status = 'loading' | 'ready' | 'error';
const TOAST_MS = 2200;
/** The game persists mid-cascade; snapshot writes are trailing-debounced (last blob wins). */
const SAVE_DEBOUNCE_MS = 1000;
/** A boot that never posts `ready` counts as a load error (AC-6). Generous — the 454 KB inline
 *  document parses in well under a second, but low-end Android WebViews deserve headroom. */
const READY_TIMEOUT_MS = 15000;

/**
 * Intent-based host surface (not raw JS injection) so the same hook drives both the native WebView
 * and the web iframe, and stays unit-testable without either. The platform hosts implement this.
 */
export interface MatchSportWebViewRef {
  applyStart: (payload: MatchSportStartPayload) => void;
  applyAudio: (payload: MatchSportAudioPayload) => void;
  reload: () => void;
}

export function useMatchSportGame(options?: { insetTop?: number }) {
  const insetTop = options?.insetTop ?? 0;
  const apiClient = useApiClient();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  // Mute is the app-wide setting (AC-19); the runtime holds a mirror, this is the source of truth.
  // The game has no in-app audio toggle, so mute only ever flows host → game.
  const isMuted = useSoundStore((s) => s.isMuted);
  const mutedRef = useRef(isMuted);
  const { track } = useAnalytics();

  const html = useMemo(() => buildMatchSportGameHtml(), []);
  const webViewRef = useRef<MatchSportWebViewRef | null>(null);
  const startedTrackedRef = useRef(false);

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [coinsLeftToday, setCoinsLeftToday] = useState(MATCH_SPORT_DAILY_COIN_CAP);
  const [capReached, setCapReached] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Session tally (this play, not all-time) — feeds the exit funnel event.
  const [levelsWon, setLevelsWon] = useState(0);
  const [sessionCoins, setSessionCoins] = useState(0);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);
  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const bindWebView = useCallback((ref: MatchSportWebViewRef | null) => { webViewRef.current = ref; }, []);

  /** Fetch today's economy + the state snapshot and boot the runtime. Called on `ready`. */
  const start = useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);
    let savedState: string | null = null;
    try { savedState = await readMatchSportState(); } catch { /* snapshot is best-effort — fail open */ }
    const payload: MatchSportStartPayload = { savedState, insetTop, muted: mutedRef.current };
    try {
      const cfg = await startMatchSportRun(apiClient);
      setCoinsLeftToday(cfg.coinsLeftToday);
      setCapReached(cfg.capReached);
    } catch {
      // Offline fallback — the game is fully playable without the server; coins just won't bank.
    }
    setStatus('ready');
    webViewRef.current?.applyStart(payload);
  }, [apiClient, insetTop]);

  /** Mirror a game persist into app storage (AC-8), trailing-debounced — last blob wins. */
  const handleSave = useCallback((state: string) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void saveMatchSportState(state); }, SAVE_DEBOUNCE_MS);
  }, []);

  const handleLevelResult = useCallback(async (msg: Extract<MatchSportBridgeMessage, { type: 'level-result' }>) => {
    if (!msg.win) {
      track(ANALYTICS_EVENTS.GAME_LEVEL_FAILED, { game_key: MATCH_SPORT_GAME_KEY, level: msg.level });
      return;
    }
    let coinsAwarded = 0; // stays 0 if the server call fails (offline / staging without routes)
    try {
      const res = await recordMatchSportLevelClear(apiClient, {
        level: msg.level,
        stars: msg.stars,
        score: msg.score,
      });
      coinsAwarded = res.coinsAwarded;
      // Coins come from the server only — anonymous users get no `coins`, so nothing is synced.
      if (typeof res.coins === 'number') syncCoins(res.coins);
      setCoinsLeftToday(res.coinsLeftToday);
      setCapReached(res.capReached);
      if (coinsAwarded > 0) flashToast(`+${coinsAwarded} coins`);
    } catch (err) {
      // Gameplay never blocks on the network — the game already advanced; just note it.
      flashToast(extractErrorMessage(err, 'Coins will catch up'));
    } finally {
      setLevelsWon((n) => n + 1);
      setSessionCoins((c) => c + coinsAwarded);
      // Progression signal — fire regardless of the server result so the drop-off funnel stays accurate.
      track(ANALYTICS_EVENTS.GAME_LEVEL_CLEARED, {
        game_key: MATCH_SPORT_GAME_KEY,
        level: msg.level,
        stars: msg.stars,
        score: msg.score,
        coins_awarded: coinsAwarded,
      });
    }
  }, [apiClient, syncCoins, flashToast, track]);

  const onMessage = useCallback((event: { nativeEvent: { data: string } }) => {
    let msg: MatchSportBridgeMessage;
    try { msg = JSON.parse(event.nativeEvent.data); } catch { return; }
    switch (msg.type) {
      case 'ready':
        if (!startedTrackedRef.current) {
          startedTrackedRef.current = true;
          track(ANALYTICS_EVENTS.GAME_STARTED, { game_key: MATCH_SPORT_GAME_KEY });
        }
        void start();
        break;
      case 'save':
        if (typeof msg.state === 'string') handleSave(msg.state);
        break;
      case 'level-result':
        void handleLevelResult(msg);
        break;
      case 'load-error':
        setErrorMessage('Could not load the game');
        setStatus('error');
        break;
      default:
        break;
    }
  }, [start, handleSave, handleLevelResult, track]);

  // Ready watchdog (AC-6): a runtime that never posts `ready` (or `load-error`) surfaces the
  // error overlay instead of an indefinite spinner. Re-arms whenever a (re)boot enters loading.
  useEffect(() => {
    if (status !== 'loading') return;
    const timer = setTimeout(() => {
      setErrorMessage('Could not load the game');
      setStatus('error');
    }, READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  // App-side mute changes (settings, or a late store hydration) reach the runtime exactly once.
  useEffect(() => {
    if (status !== 'ready') return;
    if (mutedRef.current === isMuted) return; // already applied at boot
    mutedRef.current = isMuted;
    webViewRef.current?.applyAudio({ muted: isMuted });
  }, [isMuted, status]);

  const retry = useCallback(() => {
    setStatus('loading');
    setErrorMessage(null);
    webViewRef.current?.reload(); // runtime re-posts `ready` → start() runs again
  }, []);

  /** Back pressed — close the session funnel; the view navigates away right after. */
  const confirmExit = useCallback(() => {
    track(ANALYTICS_EVENTS.GAME_COMPLETED, {
      game_key: MATCH_SPORT_GAME_KEY,
      level: levelsWon,
      coins_awarded: sessionCoins,
      reason: 'exit',
    });
  }, [track, levelsWon, sessionCoins]);

  return {
    status,
    isLoading: status === 'loading',
    isError: status === 'error',
    errorMessage,
    html,
    coinsLeftToday,
    capReached,
    toast,
    levelsWon,
    sessionCoins,
    bindWebView,
    onMessage,
    retry,
    confirmExit,
  };
}

export type UseMatchSportGame = ReturnType<typeof useMatchSportGame>;
